import { z } from 'zod';
import { IsoDateTime } from './common.ts';

/**
 * AppsFlyer install attribution (BB-131). Two ways into one model: the server postback (`POST /server/v1/integrations/
 * appsflyer/postback`, scope `attribution.import`, AppsFlyer's snake_case fields as they come) and the SDK operation
 * `install_attribution` (camelCase). An install is keyed by `appsflyer_id`; its attribution only ever upgrades
 * (organic → paid, client → server), never downgrades.
 */
export const APPSFLYER_ID_PATTERN = /^\d{13}-\d{6,25}$/;
export const AppsflyerId = z.string().trim().regex(APPSFLYER_ID_PATTERN, 'invalid_appsflyer_id');

const raw = z.string().max(2048).nullable().optional();
/** One postback record. Only these fields are read; unknown fields are dropped and never stored. */
export const AppsflyerPostbackRecord = z.object({
  appsflyer_id: z.string().max(64),
  customer_user_id: raw,
  media_source: raw,
  campaign: raw,
  af_adset: raw,
  af_ad: raw,
  install_time: raw,
  event_name: raw,
  /** `organic` marks an organic install even when `media_source` is absent (AppsFlyer Push API). */
  campaign_type: raw,
  /** `organic-install`, `organic-reinstall`, … mark organic installs. */
  event_type: raw,
});
export type AppsflyerPostbackRecord = z.infer<typeof AppsflyerPostbackRecord>;
/** One record, or `{ records: [...] }` with 1 to 100. */
export const POSTBACK_MAX_RECORDS = 100;

export const POSTBACK_OUTCOMES = ['inserted', 'upgraded', 'enriched', 'unchanged', 'organic_skipped', 'link_only', 'event_ignored'] as const;
export type PostbackOutcome = (typeof POSTBACK_OUTCOMES)[number];
export const POSTBACK_CODES = ['invalid_appsflyer_id', 'invalid_install_time', 'invalid_record', 'profile_erased', 'internal_error'] as const;

export interface PostbackResult {
  index: number;
  status: 'accepted' | 'duplicate' | 'retryable_error' | 'permanent_error';
  outcome?: PostbackOutcome;
  linked?: boolean;
  code?: (typeof POSTBACK_CODES)[number];
}
export interface PostbackResponse {
  schemaVersion: 1;
  results: PostbackResult[];
}

/** The SDK operation (member of `SdkOperation`); organic installs send only `appsflyerId`. */
export const InstallAttributionOperation = z.object({
  type: z.literal('install_attribution'),
  operationId: z.uuid(),
  occurredAt: IsoDateTime,
  appsflyerId: z.string().max(64),
  source: z.string().max(2048).optional(),
  campaign: z.string().max(2048).optional(),
  adgroup: z.string().max(2048).optional(),
  ad: z.string().max(2048).optional(),
  installAt: IsoDateTime.optional(),
});
export type InstallAttributionOperation = z.infer<typeof InstallAttributionOperation>;
